import { ReactNode, useContext, useState } from "react";
import { CoreButton, CorePaper, CoreTitleCard, CoreTitleCardHeader } from "@verity/components/core";
import { ThemeWrapper, useMobileLayout } from "@verity/components";
import i18n from "../../../i18n";
import { PortalControllerContext } from "../../context";
import { PracticeQuizButton } from "../assessments-page/practice-quiz-comp";
import CreateThirdPartyQuiz from "../assessments-page/createThirdPartyAssessment";
import styles from "./index.module.css";
import PracticeAssessment from "../../../images/portal/practice-assessment.svg?react";
import ThirdPartyAssessment from "../../../images/portal/third-party-assessment.svg?react";
import StandaloneAssessment from "../../../images/portal/standalone-assessment.svg?react";
import CreateStandaloneAssessmentSection from "../assessments-page/CreateStandaloneAssessmentSection";
import BackButton from "../../../components/back-button";
import VisualizerBanner from "../visualizer-banner";

function AssessmentTypeTile({
	image,
	title,
	description,
	button,
}: {
	image: ReactNode;
	title: ReactNode;
	description: ReactNode;
	button: ReactNode;
}) {
	const isMobileLayout = useMobileLayout();

	return (
		<CorePaper
			component="section"
			className={styles.tile}
			style={isMobileLayout ? { minHeight: "0px", padding: "10px 10px 20px 10px" } : undefined}
		>
			{isMobileLayout ? (
				<div className={`${styles.tileText} ${styles.tileTextCompact}`}>
					<div className={styles.tileHeader}>
						<div className={styles.tileImageCompact}>{image}</div>
						<CoreTitleCardHeader variant="h2">{title}</CoreTitleCardHeader>
					</div>
					{description}
				</div>
			) : (
				<>
					<div className={styles.tileImage}>{image}</div>
					<div className={styles.tileText}>
						<CoreTitleCardHeader variant="h2">{title}</CoreTitleCardHeader>
						{description}
					</div>
				</>
			)}
			{button}
		</CorePaper>
	);
}

export default function CreateAssessmentPage({
	setShowCreateAssessment,
	refreshPage,
}: {
	setShowCreateAssessment: (value: boolean) => void;
	refreshPage: () => Promise<void>;
}) {
	const { portalContext } = useContext(PortalControllerContext);
	const { lms, instituteUuid, courseUuid } = portalContext;
	const isMobileLayout = useMobileLayout();

	const [createThirdPartyDialogShowing, setCreateThirdPartyDialogShowing] = useState(false);
	// no LMS -> only standalone available
	const [createStandaloneDialogShowing, setCreateStandaloneDialogShowing] = useState(!lms);

	const tiles: ReactNode[] = [];
	if (lms === "canvas" || lms === "desire2learn") {
		tiles.push(
			<AssessmentTypeTile
				key="practice"
				image={<PracticeAssessment />}
				title={i18n.t("portal.components.createAssessmentPageTSX.practiceAssessment.title")}
				description={i18n.t("portal.components.createAssessmentPageTSX.practiceAssessment.description")}
				button={
					<PracticeQuizButton
						onFinish={() => {
							void refreshPage();
							setShowCreateAssessment(false);
						}}
					/>
				}
			/>,
		);
	}
	if (lms === "BlackboardLearn") {
		tiles.push(
			<AssessmentTypeTile
				key="thirdParty"
				image={<ThirdPartyAssessment />}
				title={i18n.t("portal.components.createAssessmentPageTSX.thirdPartyAssessment.title")}
				description={i18n.t("portal.components.createAssessmentPageTSX.thirdPartyAssessment.description")}
				button={
					<CoreButton onClick={() => setCreateThirdPartyDialogShowing(true)}>
						{i18n.t("portal.components.createAssessmentPageTSX.thirdPartyAssessment.button")}
					</CoreButton>
				}
			/>,
		);
	}
	if (instituteUuid && courseUuid) {
		tiles.push(
			<AssessmentTypeTile
				key="standalone"
				image={<StandaloneAssessment />}
				title={i18n.t("portal.components.createAssessmentPageTSX.standaloneAssessment.title")}
				description={i18n.t("portal.components.createAssessmentPageTSX.standaloneAssessment.description")}
				button={
					<CoreButton onClick={() => setCreateStandaloneDialogShowing(true)}>
						{i18n.t("portal.components.createAssessmentPageTSX.standaloneAssessment.button")}
					</CoreButton>
				}
			/>,
		);
	}

	return (
		<ThemeWrapper scopedCssBaselineProps={{ className: `${styles.page} ${isMobileLayout ? styles.pageCompact : ""}` }}>
			<VisualizerBanner />
			<div className={`${styles.pageContainer} ${isMobileLayout ? styles.pageContainerCompact : ""}`}>
				{isMobileLayout ? (
					<>
						{!createStandaloneDialogShowing && !createThirdPartyDialogShowing && (
							<CoreTitleCard
								title={i18n.t("portal.components.createAssessmentPageTSX.createAssessment")}
								description={i18n.t("portal.components.createAssessmentPageTSX.description")}
								style={{ minWidth: "300px", padding: "5px 10px 10px 10px", marginTop: "10px" }}
								slotProps={{ text: { style: { gap: "5px" } } }}
								topContent={
									<BackButton
										onBack={() => {
											void refreshPage();
											setShowCreateAssessment(false);
										}}
										ariaLabel={i18n.t("portal.components.createAssessmentPageTSX.backButtonAriaLabel")}
									/>
								}
							/>
						)}
						{createStandaloneDialogShowing && instituteUuid && courseUuid ? (
							<CreateStandaloneAssessmentSection
								instituteUuid={instituteUuid}
								courseUuid={courseUuid}
								onClose={() => {
									setCreateStandaloneDialogShowing(false);
								}}
								onFinish={() => {
									void refreshPage();
									setShowCreateAssessment(false);
								}}
							/>
						) : (
							<div className={`${styles.tiles} ${styles.tilesCompact} ${tiles.length === 4 ? "" : styles.row}`}>
								{tiles}
							</div>
						)}
					</>
				) : (
					<>
						<div style={{ marginTop: "7.5px" }}>
							<BackButton
								onBack={() => {
									void refreshPage();
									setShowCreateAssessment(false);
								}}
								ariaLabel={i18n.t("portal.components.createAssessmentPageTSX.backButtonAriaLabel")}
							/>
						</div>
						<CoreTitleCard
							title={i18n.t("portal.components.createAssessmentPageTSX.createAssessment")}
							description={i18n.t("portal.components.createAssessmentPageTSX.description")}
						/>
						{createStandaloneDialogShowing && instituteUuid && courseUuid ? (
							<CreateStandaloneAssessmentSection
								instituteUuid={instituteUuid}
								courseUuid={courseUuid}
								onClose={() => {
									setCreateStandaloneDialogShowing(false);
									if (!lms) {
										setShowCreateAssessment(false);
									}
								}}
								onFinish={() => {
									void refreshPage();
									setShowCreateAssessment(false);
								}}
							/>
						) : (
							<div className={`${styles.tiles} ${tiles.length === 4 ? "" : styles.row}`}>{tiles}</div>
						)}
					</>
				)}
				{createThirdPartyDialogShowing && (
					<CreateThirdPartyQuiz
						onClose={() => setCreateThirdPartyDialogShowing(false)}
						onFinish={() => {
							void refreshPage();
							setCreateThirdPartyDialogShowing(false);
							setShowCreateAssessment(false);
						}}
					/>
				)}
			</div>
		</ThemeWrapper>
	);
}
